import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isPortal } from './services/platform';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import MenuPage from './pages/MenuPage';
import PlayScreen from './pages/PlayScreen';
import LeaderboardPage from './pages/LeaderboardPage';
import ProfilePage from './pages/ProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LoginPage from './pages/LoginPage';
import { useAppStore } from './store/appStore';
import useNativeSetup from './hooks/useNativeSetup';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 120000, retry: 1, refetchOnWindowFocus: false } },
});

function ThemeSync() {
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
  }, [nightMode]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setNightMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setNightMode]);

  return null;
}

export default function App() {
  useNativeSetup();
  // Portal embeds (itch.io / CrazyGames / Poki) run inside an iframe at an
  // arbitrary CDN path, so BrowserRouter's paths can never match — HashRouter
  // keeps routing self-contained. Portal builds are guest-only: no login,
  // profile, or IAP routes.
  const Router = isPortal() ? HashRouter : BrowserRouter;
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeSync />
      <Router>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<MenuPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            {!isPortal() && <Route path="profile" element={<ProfilePage />} />}
            {!isPortal() && <Route path="subscribe" element={<SubscriptionPage />} />}
            {!isPortal() && <Route path="payment-success" element={<PaymentSuccessPage />} />}
          </Route>
          <Route path="play/:mode" element={<PlayScreen />} />
          {!isPortal() && <Route path="login" element={<LoginPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <Toaster
        position="top-center"
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        toastOptions={{
          duration: 3500,
          style: {
            borderRadius: '14px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '92vw',
            background: 'var(--toast-bg)',
            color: 'var(--toast-fg)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            border: '1px solid var(--toast-border)',
          },
          success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
        }}
      />
    </QueryClientProvider>
  );
}
